import { useState } from "react";

const moveSizes = [
  { value: "", label: "Size Of Move*" },
  { value: "1", label: "Studio" },
  { value: "2", label: "1 Bedroom" },
  { value: "3", label: "2 Bedrooms" },
  { value: "4", label: "3 Bedrooms" },
  { value: "5", label: "4 Bedrooms" },
  { value: "6", label: "4+ Bedrooms" },
  { value: "7", label: "Small Office" },
  { value: "8", label: "Medium Office" },
  { value: "9", label: "Large Office" },
  { value: "10", label: "Small storage (5x5, 5x8, 5x10)" },
  { value: "11", label: "Medium storage (10x10, 10x15)" },
  { value: "12", label: "Large storage (10x20)" },
];

export default function QuoteForm() {
  const [step, setStep] = useState(1);
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [formData, setFormData] = useState({
    first_name: "",
    source_zip: "",
    destination_zip: "",
    move_date: "",
    email: "",
    phone: "",
    move_size_id: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const honeypot = (e.currentTarget.querySelector("#phone_alt") as HTMLInputElement)?.value;
    if (honeypot && honeypot.trim() !== "") {
      setStatus("success");
      return;
    }
    setStatus("loading");
    setFormError("");
    const data = new FormData(e.currentTarget);
    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key !== "phone_alt" && value) payload.append(key, value as string);
    });
    try {
      const response = await fetch("", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      if (!response.ok || !parsed || parsed.code !== "OK" || serverMsg.toLowerCase().includes("spam")) {
        setStatus("error");
        setFormError(serverMsg || "Something went wrong. Please try again.");
        return;
      }
      setStatus("success");
      setFormData({ first_name: "", source_zip: "", destination_zip: "", move_date: "", email: "", phone: "", move_size_id: "" });
      setStep(1);
    } catch {
      setStatus("error");
      setFormError("Network error. Please try again.");
    }
  };

  const inputClass =
    "w-full pl-9 pr-3 py-3 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-all";
  const iconClass =
    "absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none";

  return (
    <section className="w-full bg-background-50 px-4 md:px-8 lg:px-16 py-8 md:py-10">
      <div className="max-w-6xl mx-auto bg-background-50 rounded-2xl border border-background-200 p-6 md:p-8">
        <h2 className="text-center text-lg md:text-xl font-bold text-accent-500 mb-6">
          Get a Free Quote!
        </h2>

        {status === "success" ? (
          <div className="py-6 text-center space-y-2">
            <div className="w-14 h-14 bg-primary-100 rounded-full flex items-center justify-center mx-auto">
              <div className="w-7 h-7 flex items-center justify-center text-primary-600">
                <i className="ri-checkbox-circle-line text-2xl" />
              </div>
            </div>
            <p className="text-base font-semibold text-foreground-900">Quote request submitted!</p>
            <p className="text-sm text-foreground-500">We&apos;ll be in touch shortly with your free quote.</p>
            <button
              onClick={() => setStatus("idle")}
              className="mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium underline"
            >
              Submit another request
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} data-readdy-form id="free-quote-form">
            <input type="text" id="phone_alt" name="phone_alt" className="form-honeypot" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />

            {step === 1 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                {/* Name */}
                <div className="relative">
                  <span className={iconClass}><i className="ri-user-line text-base" /></span>
                  <input
                    type="text" name="first_name" placeholder="First and Last Name*"
                    required value={formData.first_name} onChange={handleChange}
                    className={inputClass}
                  />
                </div>
                {/* From ZIP */}
                <div className="relative">
                  <span className={iconClass}><i className="ri-home-4-line text-base" /></span>
                  <input
                    type="text" name="source_zip" maxLength={5} placeholder="Moving From ZIP code*"
                    required value={formData.source_zip} onChange={handleChange}
                    className={inputClass}
                  />
                </div>
                {/* To ZIP */}
                <div className="relative">
                  <span className={iconClass}><i className="ri-map-pin-line text-base" /></span>
                  <input
                    type="text" name="destination_zip" maxLength={5} placeholder="Moving To ZIP code*"
                    required value={formData.destination_zip} onChange={handleChange}
                    className={inputClass}
                  />
                </div>
                {/* Date */}
                <div className="relative">
                  <span className={iconClass}><i className="ri-calendar-line text-base" /></span>
                  <input
                    type="date" name="move_date"
                    required value={formData.move_date} onChange={handleChange}
                    className={inputClass}
                  />
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                {/* Email */}
                <div className="relative">
                  <span className={iconClass}><i className="ri-mail-line text-base" /></span>
                  <input
                    type="email" name="email" placeholder="Your Email Address*"
                    required value={formData.email} onChange={handleChange}
                    className={inputClass}
                  />
                </div>
                {/* Phone */}
                <div className="relative">
                  <span className={iconClass}><i className="ri-phone-line text-base" /></span>
                  <input
                    type="tel" name="phone" placeholder="Your Phone Number*"
                    required value={formData.phone} onChange={handleChange}
                    className={inputClass}
                  />
                </div>
                {/* Size of Move */}
                <div className="relative">
                  <select
                    name="move_size_id" required value={formData.move_size_id} onChange={handleChange}
                    className="w-full px-3 py-3 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 appearance-none transition-all"
                  >
                    {moveSizes.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none">
                    <i className="ri-arrow-down-s-line text-base" />
                  </span>
                </div>
              </div>
            )}

            {/* Navigation row */}
            <div className="flex items-center justify-between mt-5">
              <button
                type="button" onClick={() => setStep(1)} disabled={step === 1}
                className="px-5 py-2 text-sm font-medium text-foreground-500 border border-background-300 rounded-lg hover:bg-background-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
              >
                Previous
              </button>
              <div className="flex gap-2">
                {step === 1 && (
                  <button
                    type="button" onClick={() => setStep(2)}
                    className="px-7 py-2 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors whitespace-nowrap"
                  >
                    Next
                  </button>
                )}
                {step === 2 && (
                  <button
                    type="submit" disabled={status === "loading"}
                    className="px-7 py-2 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-70 whitespace-nowrap"
                  >
                    {status === "loading" ? "Submitting…" : "Submit"}
                  </button>
                )}
              </div>
            </div>

            {status === "error" && formError && (
              <p className="mt-3 text-center text-sm text-red-600 font-medium">{formError}</p>
            )}
          </form>
        )}
      </div>
    </section>
  );
}